import getLinkHref from '@/modules/utils/api/getLinkHref'
import { pick, isUndefined, map, filter, reject, each, extend, find } from 'lodash-es'
import { api } from '@/api'
import to from '@/modules/await-to-js'

const pickEndDevice = function (item) {
  item = pick(item, ['devEui', 'cluster', 'links'])
  item.cluster = pickCluster(item.cluster)
  return item
}

const pickCluster = function (cluster) {
  return pick(cluster, ['id', 'name', 'links'])
}

const state = {
  endDevicesSelection: []
}

export const getters = {
  endDevicesSelection: state => state.endDevicesSelection,
  isEndDeviceInSelection: state => item => { return !isUndefined(find(state.endDevicesSelection, pick(item, 'devEui'))) }
}

export const mutations = {
  /* ***** Selection management ***** */

  // Set selection
  setEndDevicesSelection (state, endDevicesSelection) {
    state.endDevicesSelection = map(endDevicesSelection, pickEndDevice)
  },

  // Filter selection to keep some items (used for bulk deletion, in case of errors)
  filterEndDevicesSelection (state, itemsToKeep) {
    state.endDevicesSelection = filter(state.endDevicesSelection, (item) => itemsToKeep.indexOf(item.devEui) !== -1)
  },

  // Clear selection
  emptyEndDevicesSelection (state) {
    state.endDevicesSelection = []
  },

  // Add an item in the selection
  addEndDeviceInSelection (state, item) {
    if (!find(state.endDevicesSelection, pick(item, 'devEui'))) {
      state.endDevicesSelection.push(pickEndDevice(item))
    }
  },

  // Remove an item from the selection
  removeEndDeviceFromSelection (state, item) {
    state.endDevicesSelection = reject(state.endDevicesSelection, pick(item, 'devEui'))
  },

  /* ***** Update selection after internal/external actions ***** */

  // Moving an item from a cluster to another cluster: update item links
  moveEndDeviceInSelection (state, { item, target }) {
    let selectionItem = find(state.endDevicesSelection, pick(item, 'devEui'))

    if (selectionItem) {
      selectionItem.cluster = pickCluster(target)

      each(selectionItem.links, (link) => {
        link.href = link.href
          .replace(/(\/customers\/)\d+/, '$1' + getLinkHref(target, 'customer').match(/\/customers\/(\d+)/)[1])
          .replace(/(\/clusters\/)\d+/, '$1' + target.id)
      })
    }
  },

  // Updating a cluster: update items from this cluster
  updateClusterInSelection (state, cluster) {
    each(state.endDevicesSelection, item => {
      if (item.cluster && item.cluster.id === cluster.id) {
        item.cluster = pickCluster(cluster)
      }
    })
  },

  // Delete or detach a cluster: remove items from this cluster from the selection
  removeClusterInSelection (state, clusterId) {
    state.endDevicesSelection = reject(state.endDevicesSelection, item => { return item.cluster && item.cluster.id === clusterId })
  },

  // Moving a cluster to another customer: update items from this cluster
  moveClusterInSelection (state, { cluster, customer }) {
    each(state.endDevicesSelection, item => {
      if (item.cluster && item.cluster.id === cluster.id) {
        // Replace item links
        each(item.links, (link) => {
          link.href = link.href.replace(/(\/customers\/)\d+/, '$1' + customer.id)
        })
        // Replace cluster links
        each(item.cluster.links, (link) => {
          link.href = link.href.replace(/(\/customers\/)\d+/, '$1' + customer.id)
        })
      }
    })
  },

  // Removing a customer: remove items from this customer (look for customers/<customerId> in links)
  removeCustomerInEndDevicesSelection (state, { customer }) {
    state.endDevicesSelection = reject(state.endDevicesSelection, item => { return Number(getLinkHref(item.cluster, 'customer').match(/\/customers\/(\d+)/)[1]) === customer.id })
  }
}

export const actions = {
  // Move an item to another cluster
  async moveEndDeviceInSelection (context, options = {}) {
    const endDevice = { cluster: { id: options.target.id } }

    let error, response
    [error, response] = await to(api.patchEndDevice(options.item, endDevice, options))
    if (error || !response) return Promise.reject(error)

    context.commit('moveEndDeviceInSelection', {
      item: options.item,
      target: options.target
    })
    return Promise.resolve(response)
  },

  // Move all selection items to another cluster
  moveAllEndDevicesInSelection (context, options = {}) {
    return map(context.state.endDevicesSelection, item => {
      return context.dispatch('moveEndDeviceInSelection', extend({}, options, { item: item }))
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
